import { useEffect, useMemo, useState } from 'react';

const NOOP = () => null;

const useOnScrollBeyondElement = ({
  topPosition = 0,
  enabled = true,
  containerRef = null,
  onScrolledBeyond = NOOP,
} = {}) => {
  const [isScrolledBeyond, setIsScrolledBeyond] = useState(false);
  const [ref, setRef] = useState(null);
  const shouldTrackScroll = useMemo(() => enabled && typeof window !== 'undefined', [enabled]);

  useEffect(() => {
    const element = ref;
    let observer = null;

    if (shouldTrackScroll) {
      const root = containerRef && containerRef.current ? containerRef.current : null;
      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            const rect = entry.boundingClientRect;
            const { rootBounds } = entry;

            const topEdge = rootBounds ? rect.top - rootBounds.top : rect.top;
            const bottomEdge = rootBounds
              ? rootBounds.bottom - rect.bottom
              : (window.innerHeight || document.documentElement.clientHeight) - rect.bottom;

            if (topEdge > bottomEdge) {
              setIsScrolledBeyond(false);
              onScrolledBeyond({ isBeyond: false });
            } else {
              const isBeyond = !entry.isIntersecting;
              setIsScrolledBeyond(isBeyond);
              onScrolledBeyond({ isBeyond });
            }
          });
        },
        {
          root,
          threshold: 0,
          rootMargin: `${topPosition * -1}px 0px 0px 0px`,
        },
      );

      if (element) {
        observer.observe(element);
      }
    }

    return () => {
      if (element && shouldTrackScroll) {
        observer.unobserve(element);
      }
    };
  }, [ref, shouldTrackScroll, topPosition, containerRef, onScrolledBeyond]);

  const onSetRef = node => {
    if (node) {
      setRef(node);
      onSetRef.current = node;
    }
  };

  return { ref: onSetRef, isScrolledBeyond };
};

export default useOnScrollBeyondElement;
